import { motion, useReducedMotion } from "framer-motion";
import { fadeInUp, staggerChildren } from "../utils/motionVariants";
import { useConfig } from "../context/useConfig";
import { SafeImage } from "./SafeImage";

function Skills() {
  const { config } = useConfig();
  const skillsSection = config.skillsSection || {}; // Defensive: if missing, component still renders
  const placeholderImage = skillsSection.placeholderImage || '/placeholder.svg';

  // Animation variants (layout inspired by reference repo's stagger pattern; reimplemented)
  const reduceMotion = useReducedMotion();

  // Safe array extraction with dynamic key for re-animation
  const skills = config.skills || []; // Primary skills list
  const categories = (skillsSection.categories || []); // Category cards list
  const hasSkills = skills.length > 0;

  return (
    <section id="skills" className="relative isolate py-28 sm:py-32 bg-gradient-to-b from-emerald-50 via-white to-cyan-50 overflow-hidden">
      {/* Ambient blobs */}
      <div className="hidden sm:block absolute -top-24 -left-24 w-[30rem] h-[30rem] rounded-full bg-emerald-300/40 blur-3xl" aria-hidden />
      <div className="hidden sm:block absolute bottom-[-18%] -right-24 w-[32rem] h-[32rem] rounded-full bg-cyan-300/40 blur-3xl" aria-hidden />
      <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_1px_1px,#000_1px,transparent_0)] [background-size:22px_22px]" aria-hidden />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          className="max-w-5xl mx-auto space-y-12"
        >
          {/* Heading sourced from config.skillsSection.heading */}
          <motion.header variants={fadeInUp(0, reduceMotion ? 0 : 24)} className="text-center space-y-6">
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight gradient-text">{(skillsSection.heading || 'Skills')}</h2>
            <motion.p variants={fadeInUp(0.1, 0)} className="text-lg text-gray-600 font-medium leading-relaxed">
              {skillsSection.subheading || 'Overview of my core technical strengths.'}
            </motion.p>
            {skillsSection.intro && (
              <motion.p variants={fadeInUp(0.15, 0)} className="text-base text-gray-500 max-w-2xl mx-auto leading-relaxed">
                {skillsSection.intro}
              </motion.p>
            )}
          </motion.header>

          {/* Icon-based responsive grid layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Skills grid */}
            <motion.ul
              key={`skills-${hasSkills}-${skills.length}`}
              variants={staggerChildren(0.1, 0.035)}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: '-120px' }}
              className="grid grid-cols-2 sm:grid-cols-3 gap-6"
              aria-label="List of skills"
            >
              {skills.map((skill, index) => {
                return (
                  <motion.li
                    key={`${skill.name}-${index}`}
                    variants={fadeInUp(0.04 * index, reduceMotion ? 0 : 28)}
                    className="group relative p-6 rounded-2xl border border-gray-100 bg-white/60 backdrop-blur-md shadow-sm hover:shadow-xl hover:border-emerald-200 transition-all overflow-hidden"
                    whileHover={{ y: -4, scale: 1.02 }}
                    title={skill.description || skill.name}
                  >
                    {/* Glow / gradient accent */}
                    <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-br from-emerald-50 via-transparent to-cyan-50" />
                    <div className="relative flex flex-col items-center text-center gap-4">
                      {/* Icon container with dynamic color */}
                      <div 
                        className="w-16 h-16 rounded-xl flex items-center justify-center bg-gradient-to-br from-white to-gray-50 shadow-inner group-hover:shadow-lg transition-all"
                        style={{ 
                          borderColor: skill.color || '#10b981',
                          borderWidth: '2px',
                          borderStyle: 'solid'
                        }}
                      >
                        <i 
                          className={`${skill.icon || 'fas fa-code'} text-3xl transition-transform group-hover:scale-110`}
                          style={{ color: skill.color || '#10b981' }}
                          aria-hidden="true"
                        />
                      </div>
                      {/* Skill name */}
                      <h4 className="text-sm font-semibold text-gray-800 tracking-tight group-hover:text-emerald-700 transition-colors leading-tight">
                        {skill.name}
                      </h4>
                      {skill.description && (
                        <p className="text-[11px] text-gray-500 leading-relaxed line-clamp-2">
                          {skill.description}
                        </p>
                      )}
                    </div>
                  </motion.li>
                );
              })}
            </motion.ul>

            {/* Right visual panel: animated / interactive Mario themed card */}
            <motion.div
              initial={reduceMotion ? {} : { opacity: 0, y: 40, rotateX: 15 }}
              whileInView={reduceMotion ? {} : { opacity: 1, y: 0, rotateX: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
              className="relative"
            >
              <div className="sticky top-32">
                <motion.div
                  animate={reduceMotion ? {} : { y: [0, -14, 0, -8, 0], rotate: [0, 1.5, -1.5, 1.5, 0] }}
                  transition={{ duration: 9, repeat: Infinity, repeatType: 'loop', ease: 'easeInOut' }}
                  className="relative mx-auto w-72 h-72 sm:w-80 sm:h-80 rounded-3xl overflow-hidden shadow-xl ring-1 ring-emerald-100/70 bg-gradient-to-br from-emerald-200 via-emerald-50 to-cyan-100 flex items-center justify-center"
                  aria-label="Playful visual representation"
                >
                  {/* Background decorative pixels */}
                  <div className="absolute inset-0 opacity-40 mix-blend-overlay bg-[radial-gradient(circle_at_12px_12px,#0d9488_1.5px,transparent_0)] [background-size:36px_36px]" />
                  {/* Floating coin rings */}
                  <motion.div
                    className="absolute inset-0 flex items-center justify-center"
                    animate={reduceMotion ? {} : { rotate: [0, 360] }}
                    transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
                  >
                    <div className="w-48 h-48 rounded-full border-2 border-emerald-300/30" />
                    <div className="w-32 h-32 rounded-full border border-cyan-400/30 absolute" />
                  </motion.div>
                  {/* Mario / character gif placeholder */}
                  <SafeImage
                    src={skillsSection.animatedImage || "/hollow_knight.gif"}
                    fallbackSrc={placeholderImage}
                    alt="Hollow Knight animation"
                    className="relative z-10 w-40 h-40 object-contain drop-shadow-[0_4px_8px_rgba(0,0,0,0.25)] pointer-events-none select-none"
                  />
                  {/* Label overlay */}
                  <div className="absolute bottom-2 left-2 right-2 text-center">
                    <p className="text-xs font-semibold tracking-wide text-emerald-800/80 bg-white/60 backdrop-blur-sm rounded-full inline-block px-3 py-1 shadow-sm">
                      Leveling up skills
                    </p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Categories subsection (optional) */}
          {categories.length > 0 && (
            <motion.div
              className="mt-24"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeInUp(0.15, reduceMotion ? 0 : 24)}
            >
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-10 gradient-text">
                {skillsSection.categoriesTitle || 'Skill Categories'}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {categories.map((category, index) => (
                  <motion.div
                    key={`${category.name}-${index}`}
                    className="glass-panel glass-panel-hover p-6 rounded-2xl text-center relative overflow-hidden group"
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.55, delay: index * 0.08 }}
                    whileHover={{ y: -6 }}
                  >
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-accent-gradient-soft" />
                    <div className="relative">
                      <div className="text-4xl mb-3" aria-hidden>{category.icon}</div>
                      <h4 className="text-lg font-semibold text-gray-900 tracking-tight">
                        {category.name}
                      </h4>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
}

export default Skills;